import React, { useState } from 'react';
import { MASTER_TEST_UNIT_EDIT_FIELDS } from './constant';
import { useDispatch } from 'react-redux';
import { Form, Formik } from 'formik';
import ModalComponent from '../../../../../components/common-components/modal/ModalComponent';
import Icons from '../../../../../components/icons/Icons';
import Button from '../../../../../components/common-components/button/Button';
import { BUTTON_VARIANTS } from '../../../../../components/common-components/button/Constants';
import Input from '../../../../../components/common-components/input/Input';
import Upload from '../../../../../components/common-components/upload/Upload';
import { getValidationSchema } from '../../../../../libs/formsUtils';
import { createMasterTestUnit, updateMasterTestUnit, bulkUploadMasterTestUnit } from './MasterTestUnitSaga';
import General from '../../../../../libs/utility/General';
import { addNotifications } from '../../../../../components/common-components/toaster/ToasterSlice';
import { TOASTER_VARIANT } from '../../../../../components/common-components/toaster/MetToaster';

function MasterAddTestUnitModel({data, open, close, }) {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(data ? 'single' : 'single'); // Default to single for edit mode
    const [csvData, setCsvData] = useState([]);
    const [isProcessingCsv, setIsProcessingCsv] = useState(false);
    const [uploadKey, setUploadKey] = useState(Date.now()); // Add key to force re-render
    
    // Template file path - this should be a static XLSX file in public folder
    const templateDownloadUrl = '/templates/test_unit_template.xlsx';
    
    const fields = [
        {
            fieldName: MASTER_TEST_UNIT_EDIT_FIELDS.TEST_NAME,
            isRequired: true,
        },
        {
            fieldName: MASTER_TEST_UNIT_EDIT_FIELDS.UNIT,
            isRequired: true,
        },
        {
            fieldName: MASTER_TEST_UNIT_EDIT_FIELDS.MAIN_TEST,
            isRequired: true,
        },
    ];

    const validationSchema = getValidationSchema(fields);

    const handleFileUpload = async (file) => {
        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                       file.type === 'application/vnd.ms-excel' ||
                       file.name.endsWith('.xlsx') || 
                       file.name.endsWith('.xls');
        const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
        
        if (!isExcel && !isCSV) {
            dispatch(addNotifications({ 
                message: 'Please upload a valid Excel (.xlsx/.xls) or CSV file', 
                variant: TOASTER_VARIANT.ERROR 
            }));
            return;
        }

        try {
            // Convert file to base64 for API upload
            const base64String = await General.fileToBase64(file);
            const fileData = {
                fileName: file.name,
                fileType: isExcel ? 'excel' : 'csv',
                fileContent: base64String
            };
            
            setCsvData([{ fileData }]); // Store file data for upload
        } catch (error) {
            console.error('File processing error:', error);
            dispatch(addNotifications({ 
                message: 'Error processing file', 
                variant: TOASTER_VARIANT.ERROR 
            }));
        }
    };

    const handleBulkUpload = async () => {
        if (csvData.length === 0 || !csvData[0].fileData) {
            dispatch(addNotifications({ 
                message: 'Please upload a file first', 
                variant: TOASTER_VARIANT.ERROR 
            }));
            return;
        }

        setIsProcessingCsv(true);
        
        try {
            const payload = {
                fileData: {file:csvData[0].fileData?.fileContent},
                closeEditModal: close,
            };
            
            dispatch(bulkUploadMasterTestUnit(payload));
        } catch (error) {
            dispatch(addNotifications({ 
                message: 'Failed to process bulk upload', 
                variant: TOASTER_VARIANT.ERROR 
            }));
        } finally {
            setIsProcessingCsv(false);
        }
    };

    const renderTabHeader = () => {
        if (data) return null; // Don't show tabs for edit mode
        
        return (
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    type="button"
                    onClick={() => {
                        setActiveTab('single');
                        setCsvData([]); // Clear CSV data when switching to single
                        setUploadKey(Date.now()); // Reset upload key
                    }}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                        activeTab === 'single'
                            ? 'border-met-primary text-met-primary'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <Icons iconName="addIcon" height="16px" width="16px" />
                        Add Single Record
                    </div>
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setActiveTab('bulk');
                        setCsvData([]); // Clear CSV data when switching to bulk
                        setUploadKey(Date.now()); // Reset upload key
                    }}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
                        activeTab === 'bulk'
                            ? 'border-met-primary text-met-primary'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <Icons iconName="fileUpload" height="16px" width="16px" />
                        Bulk Upload
                    </div>
                </button>
            </div>
        );
    };

    const renderSingleRecordForm = ({ submitForm, isSubmitting, handleReset, values, setFieldValue, handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
            <div className="flex flex-col">
                <div className="w-full flex justify-between">
                    <div className="w-[45%]">
                        <Input
                            label="Test Name"
                            isRequired={true}
                            placeholder="Enter Test Name"
                            name={MASTER_TEST_UNIT_EDIT_FIELDS.TEST_NAME}
                            value={values[MASTER_TEST_UNIT_EDIT_FIELDS.TEST_NAME]}
                            onChangeCb={(e) => {
                                setFieldValue(MASTER_TEST_UNIT_EDIT_FIELDS.TEST_NAME, e.target.value);
                            }}
                        />
                    </div>
                    <div className="w-[45%]">
                        <Input
                            label="Unit"
                            isRequired={true}
                            placeholder="Enter Unit"
                            name={MASTER_TEST_UNIT_EDIT_FIELDS.UNIT}
                            value={values[MASTER_TEST_UNIT_EDIT_FIELDS.UNIT]}
                            onChangeCb={(e) => {
                                setFieldValue(MASTER_TEST_UNIT_EDIT_FIELDS.UNIT, e.target.value);
                            }}
                        />
                    </div>
                </div>
                <div className="w-full flex justify-between mt-[20px]">
                    <div className="w-[45%]">
                        <Input
                            label="Main Test"
                            isRequired={true}
                            placeholder="Enter Main Test"
                            name={MASTER_TEST_UNIT_EDIT_FIELDS.MAIN_TEST}
                            value={values[MASTER_TEST_UNIT_EDIT_FIELDS.MAIN_TEST]}
                            onChangeCb={(e) => {
                                setFieldValue(MASTER_TEST_UNIT_EDIT_FIELDS.MAIN_TEST, e.target.value);
                            }}
                        />
                    </div>
                </div>
                {/* Hidden submit button to ensure form can be submitted */}
                <button type="submit" style={{ display: 'none' }} />
            </div>
        </Form>
    );

    const renderBulkUploadForm = () => (
        <div className="flex flex-col gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">Excel Upload Instructions</h4>
                <p className="text-sm text-blue-700 mb-2">
                    Please upload an Excel (.xlsx/.xls) file with the following columns:
                </p>
                <ul className="text-sm text-blue-700 list-disc list-inside">
                    <li><strong>testName</strong> - Test name (required)</li>
                    <li><strong>unit</strong> - Unit (required)</li>
                    <li><strong>mainTest</strong> - Main test (required)</li>
                </ul>
                <div className="mt-3">
                    <a 
                        href={templateDownloadUrl}
                        download="test_unit_template.xlsx"
                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                    >
                        Download Template (XLSX)
                    </a>
                </div>
            </div>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <Upload
                    key={uploadKey} // Add key to force re-render
                    icon={<Icons iconName="fileUpload" height="48px" width="48px" color="#1A1A1A4D" />}
                    title="Drag & Drop Excel File"
                    content="Or"
                    subTitle="Browse Files"
                    customClass="w-full h-[200px]"
                    onChangeCb={handleFileUpload}
                    subContent="Supported formats: Excel (.xlsx/.xls)"
                />
            </div>
            
            {csvData.length > 0 && csvData[0].fileData && (
                <div className="bg-green-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-green-800">File Ready</h4>
                        <Button
                            variant={BUTTON_VARIANTS.TEXT}
                            customBtnClass="text-red-600 hover:text-red-800 text-sm"
                            onClickCb={() => {
                                setCsvData([]);
                                setUploadKey(Date.now()); // Force re-render of upload component
                            }}
                        >
                            Clear File
                        </Button>
                    </div>
                    <p className="text-sm text-green-700 mb-2">
                        File "{csvData[0].fileData.fileName}" is ready for upload
                    </p>
                    <p className="text-sm text-gray-600">
                        Click "Upload File" to process the data. The API will validate the file structure and content.
                    </p>
                </div>
            )}
        </div>
    );

    return (
        <Formik
            initialValues={
                {
                    [MASTER_TEST_UNIT_EDIT_FIELDS.TEST_NAME]: data?.testName || '',
                    [MASTER_TEST_UNIT_EDIT_FIELDS.UNIT]: data?.unit || '',
                    [MASTER_TEST_UNIT_EDIT_FIELDS.MAIN_TEST]: data?.mainTest || '',
                }
            }
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
                const payload = {
                    apiPayload: {
                        ...values,
                    },
                    closeEditModal: close,
                };                
                try {
                    // Use create for new records, update for existing records
                    if (data?.uuid) {
                        payload.id = data.uuid;
                        dispatch(updateMasterTestUnit(payload));
                    } else {
                        dispatch(createMasterTestUnit(payload));
                    }
                } catch (err) {
                    console.log(err);
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ submitForm, isSubmitting, handleReset, values, setFieldValue, handleSubmit, errors, touched }) => {
                return (
                    <ModalComponent
                        open={open}
                        title={data ? 'Edit Test Unit' : 'Add Test Unit'}
                        close={() => {
                            setActiveTab('single');
                            setCsvData([]);
                            setIsProcessingCsv(false);
                            setUploadKey(Date.now()); // Reset upload key when closing
                            close();
                        }}
                        icon={<Icons iconName="closeIcon" />}
                        footerButton={
                            <div className="flex gap-2">
                                {activeTab === 'bulk' ? (
                                    <Button
                                        onClickCb={handleBulkUpload}
                                        variant={BUTTON_VARIANTS.CONTAINED}
                                        customBtnClass="text-met-primary"
                                        disabled={isProcessingCsv || csvData.length === 0}
                                    >
                                        {isProcessingCsv ? 'Processing...' : 'Upload File'}
                                    </Button>
                                ) : (
                                    <Button
                                        onClickCb={() => {
                                            submitForm();
                                        }}
                                        variant={BUTTON_VARIANTS.CONTAINED}
                                        customBtnClass="text-met-primary"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : (data ? 'Update' : 'Save')}
                                    </Button>
                                )}
                            </div>
                        }
                        customClasses="w-[60%] overflow-visible"
                        enableReinitialize
                        hideOverflow={true}
                    >
                        {renderTabHeader()}
                        {activeTab === 'bulk' ? renderBulkUploadForm() : renderSingleRecordForm({ submitForm, isSubmitting, handleReset, values, setFieldValue, handleSubmit })}
                    </ModalComponent>
                );
            }}
        </Formik>
    );
}

export default MasterAddTestUnitModel; 